import React, { useState, useMemo } from 'react'

import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Badge,
    Button,
} from '@material-ui/core'
import {
    Search,
    FilterList as FilterIcon,
    Clear as ClearIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { LeadsFilters } from '../../../../hooks/Outreach/useLeads'
import { Campaign } from '../../../../models/Outreach/types'
import { FilterDialog } from './FilterDialog'

interface ProspectsSearchAndFilterProps {
    filters: LeadsFilters
    onSearchChange: (value: string) => void
    onApplyFilters: (filters: LeadsFilters) => void
    onClearFilters: () => void
    campaigns?: Campaign[]
}

const useStyles = makeStyles((theme: Theme) => ({
    searchFilterContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
    },
    searchField: {
        flex: 1,
        minWidth: '200px',
    },
}))

export const ProspectsSearchAndFilter: React.FC<ProspectsSearchAndFilterProps> = ({
    filters,
    onSearchChange,
    onApplyFilters,
    onClearFilters,
    campaigns,
}) => {
    const classes = useStyles()
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    // Count active filters (excluding is_lead which is controlled by tab)
    const activeFilterCount = useMemo(() => {
        let count = 0
        if (filters.campaign_id) count++
        if (filters.in_campaign !== undefined) count++
        if (filters.has_email !== undefined) count++
        if (filters.has_phone !== undefined) count++
        return count
    }, [filters])

    const hasActiveFilters = activeFilterCount > 0 || !!filters.search

    return (
        <>
            <Box className={classes.searchFilterContainer}>
                <TextField
                    placeholder="Search by name, email, or phone..."
                    value={filters.search || ''}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
                <Badge
                    badgeContent={activeFilterCount}
                    color="primary"
                    invisible={activeFilterCount === 0}
                >
                    <IconButton
                        onClick={() => setFilterDialogOpen(true)}
                        color={hasActiveFilters ? 'primary' : 'default'}
                    >
                        <FilterIcon />
                    </IconButton>
                </Badge>
                {hasActiveFilters && (
                    <Button
                        startIcon={<ClearIcon />}
                        onClick={onClearFilters}
                        size="small"
                    >
                        Clear Filters
                    </Button>
                )}
            </Box>
            <FilterDialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                onApply={onApplyFilters}
                onClear={onClearFilters}
                currentFilters={filters}
                campaigns={campaigns}
            />
        </>
    )
}
